import { FormEvent, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Typography, Alert, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import IconButton from "../../components/ui/IconButton";
import LoadingButton from "../../components/ui/LoadingButton";
import Input from "../../components/ui/Input";
import { PasswordCriteria } from "../../components/PasswordCriteria";
import { useIntl } from "../../contexts/IntlContext";
import { LayoutUnauthenticated } from "../../components/LayoutUnauthenticated";
import api from "../../services/api";
import { PasswordValidator } from "../../utils/passwordValidator";

export function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [passwordRules, setPasswordRules] = useState<any[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"success" | "error" | "">("");
  const [formErrors, setFormErrors] = useState({ password: "", confirmPassword: "" });

  const navigate = useNavigate();
  const { hash } = useParams();
  const intl = useIntl();

  const [tokenValid, setTokenValid] = useState(false);
  const [tokenChecking, setTokenChecking] = useState(true);
  const [redirectSeconds, setRedirectSeconds] = useState(3);

  useEffect(() => {
    if (!hash) {
      setTokenChecking(false);
      return;
    }
    api
      .get(`app/auth/token/validate/${hash}`)
      .then(() => setTokenValid(true))
      .catch((error: any) => {
        if (error.response?.data?.message) {
          setMessage(error.response.data.message);
        } else {
          setMessage(intl.formatMessage({ id: 'resetPassword.error' }));
        }
        setMessageType("error");
      })
      .finally(() => setTokenChecking(false));
  }, [intl, hash]);

  useEffect(() => {
    if (messageType !== "success") return;
    setRedirectSeconds(3);
    const interval = setInterval(() => {
      setRedirectSeconds((prev) => Math.max(prev - 1, 0));
    }, 1000);
    const timer = setTimeout(() => navigate("/login"), 3000);
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [messageType, navigate]);

  useEffect(() => {
    if (!password && !confirmPassword) {
      setPasswordRules([]);
      return;
    }
    setPasswordRules(PasswordValidator.validateWithConfirm(password, confirmPassword));
  }, [password, confirmPassword]);

  const validateForm = () => {
    let isValid = true;
    const errors = { password: "", confirmPassword: "" };

    const passwordValidation = PasswordValidator.validateWithConfirm(password, confirmPassword);
    const strengthFailed = passwordValidation.some((rule) => rule.name !== "confirm" && rule.error);
    const confirmFailed = passwordValidation.some((rule) => rule.name === "confirm" && rule.error);
    if (strengthFailed) {
      errors.password = intl.formatMessage({ id: 'validation.passwordCriteria' });
      isValid = false;
    }
    if (confirmFailed) {
      errors.confirmPassword = intl.formatMessage({ id: 'validation.passwordMismatch' });
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await api.put(`app/auth/reset-password/${hash}`, {
        password,
        confirmPassword,
      });

      setMessage(intl.formatMessage({ id: 'resetPassword.success' }));
      setMessageType("success");
    } catch (error: any) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(intl.formatMessage({ id: 'resetPassword.error' }));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <LayoutUnauthenticated
      title={intl.formatMessage({ id: 'resetPassword.title' })}
      subtitle={intl.formatMessage({ id: 'resetPassword.subtitle' })}
    >
      {!message && tokenChecking ? (
        <Alert severity="info" sx={{ mb: 2, width: "100%" }}>
          {intl.formatMessage({ id: 'resetPassword.checkingToken' })}
        </Alert>
      ) : !message ? (
        <form onSubmit={handleSubmit} style={{ width: "100%" }}>
          <Typography variant="h6">
            {intl.formatMessage({ id: 'resetPassword.newPassword' })}
          </Typography>

          {tokenValid && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {intl.formatMessage({ id: 'resetPassword.tokenValid' })}
            </Alert>
          )}

          <Input
            label={intl.formatMessage({ id: 'resetPassword.newPassword' })}
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            error={formErrors.password !== ""}
            helperText={formErrors.password}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
              formHelperText: { sx: { minHeight: '1.5em' } },
            }}
            sx={{ width: "100%", mb: 2 }}
          />

          <Input
            label={intl.formatMessage({ id: 'resetPassword.confirmPassword' })}
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            error={formErrors.confirmPassword !== ""}
            helperText={formErrors.confirmPassword}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
              formHelperText: { sx: { minHeight: '1.5em' } },
            }}
            sx={{ width: "100%", mb: 2 }}
          />

          <PasswordCriteria passwordRules={passwordRules} />

          <LoadingButton
            type="submit"
            color="primary"
            loading={loading}
            sx={{ width: "100%", mb: 2 }}
          >
            {intl.formatMessage({ id: 'resetPassword.submit' })}
          </LoadingButton>
        </form>
      ) : (
        <Alert severity={messageType === "error" ? "error" : "success"} sx={{ mb: 2 }}>
          {message}
          {messageType === "success" && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {intl.formatMessage({ id: 'resetPassword.redirectingIn' }, { seconds: redirectSeconds })}
            </Typography>
          )}
        </Alert>
      )}
      <LoadingButton
        color="secondary"
        onClick={() => navigate(messageType === "error" ? "/forgot-password" : "/login")}
        sx={{ width: "100%", mb: 2 }}
      >
        {intl.formatMessage({ id: messageType === "error" ? "resetPassword.requestNewLink" : "resetPassword.backToLogin" })}
      </LoadingButton>
    </LayoutUnauthenticated>
  );
}